/**
 * @file useUserBaseInfo.js
 * @description 基本资料页：信息编辑、头像裁剪上传
 * @module features/user/composables/useUserBaseInfo
 */

import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.css'
import { computed, nextTick, onMounted, ref } from 'vue'
import { useUserStore } from '@/stores/user'
import {
  fetchUserBaseApi,
  updateUserBaseApi,
  uploadUserAvatarApi,
} from '@/features/user/api/userApi'
import { PLACEHOLDER_ASSETS } from '@/shared/assets/manifest'

const defaultAvatar = PLACEHOLDER_ASSETS.defaultAvatar
const AVATAR_CROP_OUTPUT = 512

export function useUserBaseInfo() {
  const formData = ref({
    no: '',
    name: '',
    email: '',
    phone: '',
    sign: '',
    avatar: defaultAvatar,
    type: 0,
    vipExpirationTime: '',
  })
  const token = ref('')
  const popupMessage = ref('')
  const popupType = ref('')
  const showPopup = ref(false)
  const isSaving = ref(false)
  const isSaveSuccess = ref(false)
  const isUploadingAvatar = ref(false)
  const avatarPreviewVisible = ref(false)
  const avatarCropVisible = ref(false)
  const avatarCropObjectUrl = ref('')
  const pendingAvatarBaseName = ref('avatar')
  const avatarFileInput = ref(null)
  const cropperImageRef = ref(null)
  let cropper = null

  const displayAvatar = computed(
    () => formData.value.headUrl || formData.value.avatar || defaultAvatar
  )

  function showPopupMessage(message, type) {
    popupMessage.value = message
    popupType.value = type
    showPopup.value = true
    setTimeout(() => {
      showPopup.value = false
    }, 2000)
  }

  async function fetchBaseInfo() {
    try {
      const response = await fetchUserBaseApi()
      if (response.data.code === 200 || response.data.code === 0) {
        const vo = response.data.baseInfoVo || {}
        formData.value = {
          ...formData.value,
          ...vo,
          avatar: vo.headUrl || vo.avatar || defaultAvatar,
        }
      } else {
        showPopupMessage(response.data.errmsg || '获取用户信息失败', 'error')
      }
    } catch (error) {
      showPopupMessage(error.message || '请求失败', 'error')
    }
  }

  async function saveInfo() {
    const payload = {
      name: formData.value.name,
      phone: formData.value.phone,
      sign: formData.value.sign,
    }
    isSaving.value = true
    isSaveSuccess.value = false
    try {
      const response = await updateUserBaseApi(payload)
      if (response.data.code === 200 || response.data.code === 0) {
        isSaveSuccess.value = true
        setTimeout(() => {
          location.reload()
        }, 2000)
      } else {
        showPopupMessage(response.data.errmsg || '操作失败', 'error')
      }
    } catch (error) {
      showPopupMessage(error.message || '请求失败', 'error')
    } finally {
      isSaving.value = false
    }
  }

  function triggerAvatarFileSelect() {
    if (isUploadingAvatar.value) return
    avatarFileInput.value?.click()
  }

  function onAvatarFileSelected(event) {
    const input = event.target
    const file = input.files && input.files[0]
    input.value = ''
    if (!file) return
    if (!file.type.startsWith('image/')) {
      showPopupMessage('请选择图片文件', 'error')
      return
    }
    const base = file.name.replace(/\.[^.]+$/, '') || 'avatar'
    pendingAvatarBaseName.value = base
    if (avatarCropObjectUrl.value) {
      URL.revokeObjectURL(avatarCropObjectUrl.value)
    }
    avatarCropObjectUrl.value = URL.createObjectURL(file)
    avatarCropVisible.value = true
  }

  function destroyAvatarCropper() {
    if (cropper) {
      cropper.destroy()
      cropper = null
    }
  }

  function onAvatarCropDialogOpened() {
    nextTick(() => {
      const img = cropperImageRef.value
      if (!img) return
      const start = () => {
        destroyAvatarCropper()
        cropper = new Cropper(img, {
          aspectRatio: 1,
          viewMode: 1,
          dragMode: 'move',
          autoCropArea: 0.85,
          restore: false,
          guides: true,
          center: true,
          highlight: false,
          cropBoxMovable: true,
          cropBoxResizable: true,
          toggleDragModeOnDblclick: false,
          background: false,
        })
      }
      if (img.complete && img.naturalWidth) {
        start()
      } else {
        img.onload = () => start()
      }
    })
  }

  function onAvatarCropDialogClosed() {
    destroyAvatarCropper()
    if (avatarCropObjectUrl.value) {
      URL.revokeObjectURL(avatarCropObjectUrl.value)
      avatarCropObjectUrl.value = ''
    }
  }

  function cancelAvatarCrop() {
    avatarCropVisible.value = false
  }

  function confirmAvatarCrop() {
    if (!cropper) {
      showPopupMessage('请等待图片加载完成', 'error')
      return
    }
    const canvas = cropper.getCroppedCanvas({
      width: AVATAR_CROP_OUTPUT,
      height: AVATAR_CROP_OUTPUT,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
    })
    if (!canvas) {
      showPopupMessage('裁剪失败，请重试', 'error')
      return
    }
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          showPopupMessage('生成图片失败', 'error')
          return
        }
        const name = `${pendingAvatarBaseName.value}-avatar.jpg`
        const file = new File([blob], name, { type: 'image/jpeg' })
        avatarCropVisible.value = false
        uploadAvatarFile(file)
      },
      'image/jpeg',
      0.92
    )
  }

  async function uploadAvatarFile(file) {
    const fd = new FormData()
    fd.append('file', file)
    isUploadingAvatar.value = true
    try {
      const response = await uploadUserAvatarApi(fd)
      const ok = response.data.code === 200 || response.data.code === 0
      if (ok) {
        const headUrl = response.data.data?.headUrl
        if (headUrl) {
          formData.value.headUrl = headUrl
          formData.value.avatar = headUrl
          useUserStore().setUser({ headUrl, avatar: headUrl })
          showPopupMessage('头像上传成功', 'success')
          setTimeout(() => {
            location.reload()
          }, 500)
        } else {
          showPopupMessage('上传成功但未返回头像地址', 'error')
        }
      } else {
        showPopupMessage(
          response.data.msg || response.data.errmsg || '上传失败',
          'error'
        )
      }
    } catch (error) {
      showPopupMessage(
        error.response?.data?.msg ||
          error.response?.data?.errmsg ||
          error.message ||
          '上传失败',
        'error'
      )
    } finally {
      isUploadingAvatar.value = false
    }
  }

  onMounted(() => {
    token.value = JSON.parse(localStorage.getItem('user'))?.token || ''
    if (!token.value) {
      console.error('未找到用户授权信息，请登录后重试')
      return
    }
    fetchBaseInfo()
  })

  return {
    formData,
    displayAvatar,
    popupMessage,
    popupType,
    showPopup,
    isSaving,
    isSaveSuccess,
    isUploadingAvatar,
    avatarPreviewVisible,
    avatarCropVisible,
    avatarCropObjectUrl,
    avatarFileInput,
    cropperImageRef,
    saveInfo,
    triggerAvatarFileSelect,
    onAvatarFileSelected,
    onAvatarCropDialogOpened,
    onAvatarCropDialogClosed,
    cancelAvatarCrop,
    confirmAvatarCrop,
  }
}
